/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { Channel, Ducks, Minutes, Program, Seconds, Specs, Time, TimeUtils } from '@wit/vtv-x-foundation-lib';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useResizeObserver } from 'hooks/useResizeObserver';
import useOnScreen from 'hooks/useOnScreen';
import { useSelector } from 'react-redux';
import TVGuideLine from './TVGuideLine';
import TVGuideTimeBar from './TVGuideTimeBar';
import { ProgramInfoBar } from '../..';

interface Props {
  channels: Channel[];
  day: Time;
  setSelectedDay: (arg: Time) => void;
  menuItemId?: number;
}

export interface TimeLimits {
  start: Time;
  end: Time;
}

const MAX_UNIT_SIZE = 215;
const UNIT_DURATION: Minutes = 30;
const CHANNELS_WINDOW = 7;
const CHANNELS_BUFFER_WINDOW = Specs.LOOK_AHEAD_CHANNELS;
const CHANNEL_WIDTH = 160;
const CHANNEL_HEIGHT = 84;
const CHANNEL_GAP = 8;
const TVGUIDE_MARKER_WIDTH = 16;
const TVGUIDE_MARKER_ERROR_MARGIN = 3; // Give the tvguide marker a margin for fast scrolls
const TIME_BAR_HEIGHT = 64;

function useFetch(channels: Channel[], page: number) {
  const [list, setList] = useState<Channel[]>([]);

  const requestPages = useCallback(async () => {
    setList(channels.slice(0, CHANNELS_WINDOW + CHANNELS_BUFFER_WINDOW + (page - 1)));
  }, [channels, page]);

  useEffect(() => {
    requestPages();
  }, [requestPages, channels, page]);

  return { list };
}

function TVGuideGrid({ channels, day, setSelectedDay, menuItemId }: Props): JSX.Element {
  const [timeLimits, setTimeLimits] = useState<TimeLimits | undefined>();
  const [unitSize, setUnitSize] = useState(MAX_UNIT_SIZE);
  const ref = useRef<HTMLDivElement>(null);
  const loader = useRef<HTMLDivElement>(null);
  const loaderMarginBottom = useRef(CHANNELS_BUFFER_WINDOW * (CHANNEL_HEIGHT + CHANNEL_GAP) - CHANNEL_GAP);
  const loaderMarginTop = useRef(loaderMarginBottom.current - TIME_BAR_HEIGHT); // since time bar is part of the 'tv guide grid content', remove this height to avoid loading more rows than necessary
  const { width } = useResizeObserver(ref);
  const [page, setPage] = useState(1);
  const { list } = useFetch(channels, page);
  const [nowPosition, setNowPosition] = useState(0);
  const childRef = useRef<HTMLDivElement>(null);
  const onScreenOptions = useRef({
    rootMargin: `0px 0px 0px -${CHANNEL_WIDTH + TVGUIDE_MARKER_WIDTH + TVGUIDE_MARKER_ERROR_MARGIN}px`,
  });
  const isVisible = useOnScreen(childRef, onScreenOptions.current);
  const [program, setProgram] = useState<Program | undefined>();
  const [showPIBar, setShowPIBar] = useState(false);
  const [now] = useState(TimeUtils.now());

  const epgBackwardDuration = useSelector(Ducks.Epg.Selectors.getEpgBackwardDuration);
  const epgForwardDuration = useSelector(Ducks.Epg.Selectors.getEpgForwardDuration);

  const negativeTimeSpan = TimeUtils.subtractDuration(now, epgBackwardDuration);
  const positiveTimeSpan = TimeUtils.addDuration(now, epgForwardDuration);

  useEffect(() => {
    const dayStart = TimeUtils.setLocalDayStart(day);
    const dayEnd = TimeUtils.addDays(dayStart, 1);
    setTimeLimits({ start: dayStart, end: dayEnd });
  }, [day]);

  const loadMoreChannels = useCallback((bufferSize = CHANNELS_BUFFER_WINDOW) => {
    setPage((prev) => prev + bufferSize);
  }, []);

  const slowLoaderObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadMoreChannels(1);
      }
    },
    [loadMoreChannels]
  );

  const fastLoaderObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadMoreChannels();
      }
    },
    [loadMoreChannels]
  );

  useEffect(() => {
    if (loader.current) {
      setPage(1);

      const option = {
        root: ref.current,
        rootMargin: `0px 0px ${loaderMarginBottom.current}px 0px`,
        threshold: 0,
      };

      const observer = new IntersectionObserver(slowLoaderObserver, option);
      observer.observe(loader.current);
      const node = loader.current;
      return () => observer.unobserve(node);
    }
    return undefined;
  }, [channels, slowLoaderObserver]);

  useEffect(() => {
    if (loader.current) {
      setPage(1);

      const option = {
        root: ref.current,
        rootMargin: '0px',
        threshold: 0,
      };

      const observer = new IntersectionObserver(fastLoaderObserver, option);
      observer.observe(loader.current);
      const node = loader.current;
      return () => observer.unobserve(node);
    }
    return undefined;
  }, [channels, fastLoaderObserver]);

  function isLoaderVisible(): boolean | undefined {
    if (loader.current && ref.current) {
      // check if the top of the loader is inside the visible part of the grid
      const loaderRect = loader.current.getBoundingClientRect();
      const gridRect = ref.current.getBoundingClientRect();
      return loaderRect.top >= gridRect.top && loaderRect.top <= gridRect.bottom;
    }
    return undefined;
  }

  useEffect(() => {
    // After loading a page check if the loader is still visible (for the case when the IntersectionObserver can't keep up with the scroll speed)
    if (page > 1) {
      if (isLoaderVisible()) {
        loadMoreChannels();
      }
      // If the user is still able to break the loading, wait 3 seconds and load more items to recover the loading process
      setTimeout(() => {
        if (isLoaderVisible()) {
          loadMoreChannels();
        }
      }, 3000);
    }
  }, [loadMoreChannels, page]);

  useEffect(() => {
    let newUnitSize = width * 0.25;
    newUnitSize = newUnitSize > MAX_UNIT_SIZE ? MAX_UNIT_SIZE : newUnitSize;
    setUnitSize(newUnitSize);
  }, [width]);

  const snapIntoPlace = useCallback(
    (today?: boolean) => {
      if (ref && timeLimits) {
        let tempDay = day;
        if (today) {
          tempDay = TimeUtils.now();
          if (!TimeUtils.isToday(day)) {
            setSelectedDay(TimeUtils.now());
          }
        }
        const nowSec: Seconds = TimeUtils.toUnixEpoch(tempDay) - TimeUtils.toUnixEpoch(timeLimits?.start);
        let newLeft = (nowSec * unitSize) / TimeUtils.minutesToSeconds(UNIT_DURATION);
        newLeft -= (width - CHANNEL_WIDTH) / 2;
        ref.current?.scrollTo({ left: newLeft > 0 ? newLeft : 0, behavior: 'smooth' });
      }
    },
    [day, timeLimits, unitSize, width, setSelectedDay]
  );

  useLayoutEffect(() => {
    snapIntoPlace();
  }, [snapIntoPlace]);

  useEffect(() => {
    if (timeLimits) {
      const nowSeconds: Seconds = TimeUtils.toUnixEpoch(now) - TimeUtils.toUnixEpoch(timeLimits.start);
      setNowPosition((nowSeconds * unitSize) / TimeUtils.minutesToSeconds(UNIT_DURATION));
    }
  }, [now, timeLimits, unitSize]);

  const scrollLeft = useCallback(() => {
    if (ref.current) {
      if (ref.current.scrollLeft === 0) {
        if (negativeTimeSpan === undefined || !TimeUtils.isSameDay(negativeTimeSpan, day)) {
          setSelectedDay(TimeUtils.setLocalDayEnd(TimeUtils.subDays(day, 1)));
        }
      }
      ref.current.scrollBy({
        left: -unitSize,
        behavior: 'smooth',
      });
    }
  }, [day, negativeTimeSpan, setSelectedDay, unitSize]);

  const scrollRight = useCallback(() => {
    if (ref.current) {
      if (ref.current.scrollLeft + ref.current.clientWidth >= ref.current.scrollWidth) {
        if (positiveTimeSpan === undefined || !TimeUtils.isSameDay(positiveTimeSpan, day)) {
          setSelectedDay(TimeUtils.setLocalDayStart(TimeUtils.addDays(day, 1)));
        }
      }
      ref.current.scrollBy({
        left: unitSize,
        behavior: 'smooth',
      });
    }
  }, [day, positiveTimeSpan, setSelectedDay, unitSize]);

  const onProgramClick = useCallback((asset: Program) => {
    setProgram(asset);
    setShowPIBar(true);
  }, []);

  const onPIBarClose = useCallback(() => {
    setShowPIBar(false);
  }, []);

  return (
    <>
      <div className="tv-guide-grid" id="TvGuideGrid">
        {!isVisible && (
          <div className="tv-guide-marker" onClick={() => snapIntoPlace(true)} id="TvGuideGrid_marker">
            <div className="white-oval" />
            <div className="red-oval" />
          </div>
        )}
        <div style={{ position: 'absolute', width: '100%' }}>
          <button
            className="tv-guide-button left-button"
            type="button"
            onClick={scrollLeft}
            id="TvGuideGrid_button___scrollLeft"
          >
            {' '}
          </button>
          <button
            className="tv-guide-button right-button"
            type="button"
            onClick={scrollRight}
            id="TvGuideGrid_button___scrollRight"
          >
            {' '}
          </button>
        </div>
        <div className="tv-guide-grid-scroll" ref={ref}>
          <div id="TvGuideGrid_content" className="tv-guide-grid-content">
            {timeLimits ? (
              <>
                {TimeUtils.isToday(day) ? (
                  <div
                    style={{ left: nowPosition + CHANNEL_WIDTH, opacity: isVisible ? 1 : 0 }}
                    className="tv-guide-live-point"
                    ref={childRef}
                  >
                    <div className="oval" />
                    <div className="point-line" />
                  </div>
                ) : null}
                <TVGuideTimeBar timeLimits={timeLimits} unitDuration={UNIT_DURATION} unitSize={unitSize} />
                {list.map((channel) => (
                  <TVGuideLine
                    key={channel.epgId}
                    channel={channel}
                    timeLimits={timeLimits}
                    time={day}
                    now={now}
                    unitDuration={UNIT_DURATION}
                    unitSize={unitSize}
                    gridRef={ref}
                    onProgramClick={onProgramClick}
                    loaderMarginTop={loaderMarginTop.current}
                    loaderMarginBottom={loaderMarginBottom.current}
                  />
                ))}
              </>
            ) : null}
            {channels.length !== list.length ? <div ref={loader} className="tv-guide-line-loader" /> : null}
          </div>
        </div>
      </div>
      {showPIBar ? <ProgramInfoBar program={program} onClose={onPIBarClose} menuItemId={menuItemId} /> : null}
    </>
  );
}

export default React.memo(TVGuideGrid);
